import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { NpcNoyauPill } from '@/components/admin/npc/npc-card';
import { NpcFacePill } from '@/components/admin/npc/npc-face-pill';
import { faceToExpected } from '@/lib/npc-expected-synthesis';
import { eurosLabel, fractionLabel } from '@/lib/npc-replays-labels';
import {
  type RunGroup,
  groupExpectedSummary,
  runGroups,
  runHasSession,
  runIssue,
} from '@/lib/npc-series-page';
import type { NpcSeriesDetail, NpcSeriesRun } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';

/**
 * Le tableau d'une série (npc.md §9, §10 ; maquette `Serie`) : une ligne par rejeu, GROUPÉ par NPC
 * puis par passe. Chaque ligne dit ce que la session a enregistré et rien de plus (N10) : l'issue ou
 * le tour et l'étape d'un rejeu qui joue, ou son rang dans la file du dev ; les étapes, les
 * objectifs et le noyau retenu, puis - depuis #216 (§26.5, lot L) - la colonne « Face à l'attendu » ;
 * les tours ; le coût du dispositif et, dans sa colonne, celui du joueur (H15). L'en-tête du groupe
 * de chaque NPC porte sa ligne de synthèse (« attendu SGP · trouvé PSG × 2, identique × 0 »). Rien
 * ne compare cette série à une autre, et il n'y a pas de grille ici : elle vit sur la fiche.
 *
 * Un rejeu interrompu que « Relancer » a rejoué RESTE, avec « relancée à 14:41 », et la ligne neuve
 * se pose juste dessous. Un emplacement sans session - purgée, ou pas encore prise par Claude - dit
 * pourquoi au lieu de porter des liens qui ne mènent nulle part.
 */

export interface SeriesRunsTableProps {
  series: NpcSeriesDetail;
}

// The table of the design system (#216, npc.md §26.7): headers in 14px Regular `--text-secondary`,
// cells in 14px, rules in `--border-default` - gray-700 and gray-200 are those two tokens.
const HEAD = 'px-3 py-2.5 text-left font-normal text-gray-700 text-small whitespace-nowrap';
const CELL = 'px-3 py-3 align-top text-small';

export function SeriesRunsTable({ series }: SeriesRunsTableProps) {
  if (series.runs.length === 0) {
    return (
      <p data-testid="npc-serie-vide" className="text-gray-700 text-small">
        Aucun rejeu encore · 0 sur {series.slotCount}. Ils arrivent ici quand Claude les démarre par{' '}
        <code className="font-mono text-caption">start_replay</code> avec l’identifiant de la série.
        La page se rafraîchit seule.
      </p>
    );
  }
  const groups = runGroups(series.runs);
  return (
    <div className="overflow-x-auto">
      <table data-testid="npc-serie-rejeux" className="w-full min-w-[60rem] border-collapse">
        <thead>
          <tr className="border-gray-200 border-b">
            {['Passe', 'Issue'].map((label) => (
              <th key={label} scope="col" className={HEAD}>
                {label}
              </th>
            ))}
            {['Étapes', 'Objectifs'].map((label) => (
              <th key={label} scope="col" className={`${HEAD} text-right`}>
                {label}
              </th>
            ))}
            <th scope="col" className={HEAD}>
              Noyau
            </th>
            <th scope="col" className={HEAD}>
              Face à l’attendu
            </th>
            {['Tours', 'Dispositif', 'Joueur'].map((label) => (
              <th key={label} scope="col" className={`${HEAD} text-right`}>
                {label}
              </th>
            ))}
            <th scope="col" className={HEAD}>
              <span className="sr-only">Gestes</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((group) => [
            <GroupHeading key={`groupe-${group.npcId}`} group={group} />,
            ...group.runs.map((run) => <RunRow key={run.id} run={run} series={series} />),
          ])}
        </tbody>
      </table>
    </div>
  );
}

/** Columns of the table: the header of a group spans them all. */
const COLUMN_COUNT = 10;

/**
 * The header of a group: the NPC (a link to its sheet) and its version, then the line of synthesis
 * of §26.5 - the expected noyau in a pill outlined in dashes and the counts of this series - or
 * « pas de noyau attendu sur sa fiche ». A group none of whose slots has a replay yet says nothing
 * of the sheet: it cannot know what it expects.
 */
function GroupHeading({ group }: { group: RunGroup }) {
  const summary = groupExpectedSummary(group);
  return (
    <tr data-testid="npc-serie-groupe" data-npc={group.npcId}>
      <th
        scope="rowgroup"
        colSpan={COLUMN_COUNT}
        className="border-gray-200 border-b bg-gray-50 px-3 py-3 text-left font-normal"
      >
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-semibold text-body">
            <Link
              to="/admin/npc/$npcId"
              params={{ npcId: group.npcId }}
              data-testid="npc-serie-rejeu-npc"
              className="text-bleu-nuit underline-offset-2 hover:underline"
            >
              {group.npcName}
            </Link>
            <span className="font-normal text-gray-700"> · v{group.versionNumber}</span>
          </span>
          {summary.known ? (
            <span
              data-testid="npc-serie-groupe-synthese"
              className="inline-flex flex-wrap items-center gap-2 text-gray-700 text-small"
            >
              {summary.expected === null ? (
                'pas de noyau attendu sur sa fiche'
              ) : (
                <>
                  attendu <NpcNoyauPill expected={summary.expected} size="caption" />
                  {summary.text === null ? null : <> · {summary.text}</>}
                </>
              )}
            </span>
          ) : null}
        </span>
      </th>
    </tr>
  );
}

function RunRow({ run, series }: { run: NpcSeriesRun; series: NpcSeriesDetail }) {
  const issue = runIssue(run, series);
  const replay = run.replay;
  return (
    <tr
      data-testid="npc-serie-rejeu"
      data-state={run.state}
      data-outcome={run.outcome ?? ''}
      className="border-gray-200 border-b"
    >
      <td className={CELL}>{run.pass}</td>
      <td className={CELL}>
        <StatusBadge
          tone={issue.tone}
          size="caption"
          pulse={run.state === 'running'}
          data-testid="npc-serie-rejeu-issue"
        >
          {issue.label}
        </StatusBadge>
        {issue.detail === null ? null : (
          <span
            data-testid="npc-serie-rejeu-detail"
            className="mt-1 block text-caption text-gray-700"
          >
            {issue.detail}
          </span>
        )}
      </td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {fractionLabel(replay?.stepsReached ?? null, replay?.stepsTotal ?? null)}
      </td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {fractionLabel(replay?.objectivesValidated ?? null, replay?.objectivesTotal ?? null)}
      </td>
      <td className={CELL}>
        {replay?.noyau === null || replay === null ? (
          '-'
        ) : (
          <NpcNoyauPill expected={replay.noyau} size="caption" />
        )}
      </td>
      <td className={CELL}>
        {replay === null ? (
          '-'
        ) : (
          <NpcFacePill face={faceToExpected(replay)} testId="npc-serie-rejeu-face" />
        )}
      </td>
      <td className={`${CELL} text-right`}>{replay === null ? '-' : replay.turns}</td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {replay === null ? '-' : eurosLabel(replay.costEur)}
      </td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {replay === null || series.player.kind === 'claude'
          ? '-'
          : eurosLabel(replay.playerCostEur)}
      </td>
      <td className={`${CELL} whitespace-nowrap text-right`}>
        {runHasSession(run) ? (
          <>
            <Link
              to="/admin/npc/regarder/$id"
              params={{ id: run.sessionId }}
              data-testid="npc-serie-rejeu-regarder"
              className="text-bleu-nuit underline underline-offset-2"
            >
              Regarder
            </Link>
            <span aria-hidden="true"> · </span>
            <Link
              to="/admin/sessions/$sessionId"
              params={{ sessionId: run.sessionId }}
              data-testid="npc-serie-rejeu-ouvrir"
              className="text-bleu-nuit underline underline-offset-2"
            >
              {run.state === 'running' ? 'Ouvrir' : 'Ouvrir la session'}
            </Link>
          </>
        ) : run.state === 'done' ? (
          <span className="text-caption text-gray-700">session supprimée</span>
        ) : null}
      </td>
    </tr>
  );
}
